import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Dropdown, Layout, Menu, Space, Tag, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  Activity, Ban, Bot, ChevronDown, CircleGauge, Database, GalleryHorizontalEnd, History,
  LayoutDashboard, ListChecks, LogOut, Megaphone, Menu as MenuIcon, MessageSquareText, RadioTower,
  Settings, ShieldCheck, Target, Users, Waypoints,
} from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { postJson } from '../api';
import { useUiStore } from '../store';
import type { AdminUser } from '../types';

const { Header, Sider, Content } = Layout;

const navItems: NonNullable<MenuProps['items']> = [
  { key: '/', icon: <LayoutDashboard size={17} />, label: '控制台' },
  { key: 'prediction', type: 'group', label: '预测中心', children: [
    { key: '/predictions', icon: <Target size={17} />, label: '预测与开奖记录' },
    { key: '/statistics', icon: <Activity size={17} />, label: '预测统计' },
  ] },
  { key: 'subscription', type: 'group', label: '订阅', children: [
    { key: '/subscriptions', icon: <Users size={17} />, label: '订阅管理' },
    { key: '/private-users', icon: <MessageSquareText size={17} />, label: '私聊用户' },
    { key: '/groups', icon: <Waypoints size={17} />, label: '群组管理' },
    { key: '/channels', icon: <RadioTower size={17} />, label: '频道管理' },
  ] },
  { key: 'delivery', type: 'group', label: '推送', children: [
    { key: '/broadcasts', icon: <Megaphone size={17} />, label: '群发消息' },
    { key: '/deliveries', icon: <ListChecks size={17} />, label: '推送任务与记录' },
    { key: '/failures', icon: <History size={17} />, label: '失败任务' },
    { key: '/media', icon: <GalleryHorizontalEnd size={17} />, label: '轮播图管理' },
    { key: '/buttons', icon: <MenuIcon size={17} />, label: '按钮管理' },
    { key: '/messages', icon: <MessageSquareText size={17} />, label: '消息模板' },
  ] },
  { key: 'system', type: 'group', label: '系统', children: [
    { key: '/bots', icon: <Bot size={17} />, label: 'SafeW 机器人' },
    { key: '/yu28', icon: <Database size={17} />, label: 'YU28 接口' },
    { key: '/blacklist', icon: <Ban size={17} />, label: '黑名单' },
    { key: '/logs', icon: <History size={17} />, label: '系统日志' },
    { key: '/admins', icon: <ShieldCheck size={17} />, label: '管理员' },
    { key: '/settings', icon: <Settings size={17} />, label: '系统设置' },
  ] },
];

const titles: Record<string, string> = {
  '/': '控制台', '/predictions': '预测与开奖记录', '/statistics': '预测统计', '/subscriptions': '订阅管理',
  '/private-users': '私聊用户', '/groups': '群组管理', '/channels': '频道管理', '/deliveries': '推送任务与记录',
  '/broadcasts': '群发消息',
  '/failures': '失败任务', '/media': '轮播图管理', '/buttons': '按钮管理', '/messages': '消息模板',
  '/bots': 'SafeW 机器人', '/yu28': 'YU28 接口', '/blacklist': '黑名单', '/logs': '系统日志',
  '/admins': '管理员', '/settings': '系统设置',
};

export function AppLayout({ user }: { user: AdminUser }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { sidebarCollapsed, setSidebarCollapsed } = useUiStore();
  const title = titles[location.pathname] ?? '管理台';
  const profileItems = useMemo<NonNullable<MenuProps['items']>>(() => [{
    key: 'logout', icon: <LogOut size={16} />, label: '退出登录',
    onClick: async () => { await postJson('/api/auth/logout'); queryClient.clear(); navigate('/login'); },
  }], [navigate, queryClient]);

  return (
    <Layout className="app-shell">
      <Sider width={244} collapsedWidth={72} collapsed={sidebarCollapsed} breakpoint="lg" onCollapse={setSidebarCollapsed} className="sidebar">
        <div className="brand">
          <div className="brand-mark"><CircleGauge size={23} /></div>
          {!sidebarCollapsed && <div><strong>木木预测</strong><span>运营管理台</span></div>}
        </div>
        <Menu theme="dark" mode="inline" selectedKeys={[location.pathname]} items={navItems} onClick={({ key }) => key.startsWith('/') && navigate(key)} className="nav-menu" />
      </Sider>
      <Layout>
        <Header className="topbar">
          <Space size={14}>
            <Button type="text" className="collapse-button" icon={<MenuIcon size={19} />} onClick={() => setSidebarCollapsed(!sidebarCollapsed)} aria-label="收起或展开导航" />
            <div>
              <Typography.Title level={4}>{title}</Typography.Title>
              <Typography.Text type="secondary">加拿大28 · 实时运营</Typography.Text>
            </div>
          </Space>
          <Dropdown menu={{ items: profileItems }} trigger={['click']}>
            <Button type="text" className="profile-button">
              <Avatar size={32}>{user.username.slice(0, 1).toUpperCase()}</Avatar>
              <span className="profile-name">{user.username}</span>
              <Tag color={user.role === 'SUPER_ADMIN' ? 'red' : 'blue'}>{user.role === 'SUPER_ADMIN' ? '超级管理员' : user.role === 'ADMIN' ? '管理员' : '查看员'}</Tag>
              <ChevronDown size={15} />
            </Button>
          </Dropdown>
        </Header>
        <Content className="page-content"><Outlet /></Content>
      </Layout>
    </Layout>
  );
}
